import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  Card,
  CardContent,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Dashboard,
  ArrowBack,
  Verified,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ref, get, child } from 'firebase/database';
import { database } from '../firebase/Config';
import { useAuth } from '../context/AuthContext';

const MyLicenses = () => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (currentUser) {
      fetchMyLicenses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const fetchMyLicenses = async () => {
    setLoading(true);
    try {
      const dbRef = ref(database);
      
      // Buscar todas as licenças
      const licensesSnapshot = await get(child(dbRef, 'licenses'));
      
      if (licensesSnapshot.exists()) {
        const licensesData = licensesSnapshot.val();
        
        // Filtrar apenas licenças do usuário logado (usando userEmail conforme estrutura das licenças)
        const myLicensesArray = Object.keys(licensesData)
          .map((key) => ({
            id: key,
            ...licensesData[key],
          }))
          .filter(license => license.userEmail === currentUser?.email);

        setLicenses(myLicensesArray);
      } else {
        setLicenses([]);
      }
    } catch (error) {
      console.error('Erro ao buscar licenças:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('pt-BR');
  };

  // Função para verificar status da licença
  const getLicenseStatus = (expirationDate) => {
    if (!expirationDate) return 'pending';
    const today = new Date();
    const expDate = new Date(expirationDate + 'T00:00:00');
    const diffTime = expDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'expired';
    if (diffDays <= 30) return 'expiring';
    return 'active';
  };

  const columns = [
    {
      field: 'pluginName',
      headerName: 'Plugin',
      width: 250,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'requestCode',
      headerName: 'Código da Requisição',
      width: 200,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'key',
      headerName: 'Chave da Licença',
      width: 250,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'date',
      headerName: 'Data de Cadastro',
      width: 150,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'expirationDate',
      headerName: 'Data de Expiração',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const status = getLicenseStatus(params.row.expirationDate);
        const statusConfig = {
          active: { label: 'Ativa', color: '#48bb78', icon: <CheckCircle /> },
          expiring: { label: 'Expirando', color: '#f6ad55', icon: <HourglassEmpty /> },
          expired: { label: 'Expirada', color: '#e53935', icon: <ErrorIcon /> },
          pending: { label: 'Pendente', color: '#718096', icon: <HourglassEmpty /> },
        };

        const config = statusConfig[status] || statusConfig.pending;

        return (
          <Chip
            icon={config.icon}
            label={config.label}
            size="small"
            sx={{
              bgcolor: config.color,
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          />
        );
      },
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      {/* Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: 'linear-gradient(90deg, #48bb78 0%, #38a169 100%)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <IconButton
            onClick={() => navigate('/home')}
            sx={{
              color: '#fff',
              mr: 2,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
            }}
          >
            <ArrowBack />
          </IconButton>
          <Verified sx={{ mr: 2, fontSize: 32 }} />
          <Typography
            variant="h5"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            Minhas Licenças
          </Typography>
          <Chip
            icon={<Dashboard />}
            label={`${licenses.length} ${licenses.length === 1 ? 'Licença' : 'Licenças'}`}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: '#fff',
              fontWeight: 600,
            }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Card de Informação */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
            color: '#fff',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Verified sx={{ fontSize: 48 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Suas Licenças de Plugins
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Visualize todas as suas licenças ativas, expirando e expiradas
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* DataGrid */}
        <Card
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={60} sx={{ color: '#48bb78' }} />
              </Box>
            ) : licenses.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                {/* Mensagem informativa */}
                <Alert
                  severity="info"
                  icon={<Verified />}
                  sx={{
                    borderRadius: 2,
                    fontSize: '1rem',
                  }}
                >
                  Você ainda não possui licenças cadastradas. Entre em contato com o administrador para obter uma licença.
                </Alert>
              </Box>
            ) : (
              <DataGrid
                rows={licenses}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                disableSelectionOnClick
                autoHeight
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f7fafc',
                    borderRadius: '8px 8px 0 0',
                    fontWeight: 700,
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#f7fafc',
                  },
                }}
              />
            )}
          </CardContent>
        </Card>

        {/* Legenda de Status */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Chip
            icon={<CheckCircle />}
            label="Ativa"
            size="small"
            sx={{ bgcolor: '#48bb78', color: '#fff', fontWeight: 600 }}
          />
          <Chip
            icon={<HourglassEmpty />}
            label="Expirando (≤ 30 dias)"
            size="small"
            sx={{ bgcolor: '#f6ad55', color: '#fff', fontWeight: 600 }}
          />
          <Chip
            icon={<ErrorIcon />}
            label="Expirada"
            size="small"
            sx={{ bgcolor: '#e53935', color: '#fff', fontWeight: 600 }}
          />
        </Box>
      </Container>

      {/* Snackbar para Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MyLicenses;
